import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { formatApiError } from "../api";
import { BrutalButton, BrutalInput } from "./Brutal";
import { Scales, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

// v414.93 — God Mode stock reconciliation. The owner enters the PHYSICAL
// count for each product a holder carries; every difference is settled
// against master through the existing audited flows:
//   counted < ReUP  →  POST /products/return  (stock moves up to master)
//   counted > ReUP  →  POST /products/assign  (consignment pull from master)
// Nothing is mutated directly — each line lands in the Movement Log and the
// Settlement ledger exactly like a manual Return / Give would.
export default function ReconcileDialog({ distributorId, distributorName, products, onClose, onDone }) {
  const { t } = useTranslation();
  const [rows, setRows] = useState(() =>
    [...products]
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
      .map((p) => ({
        childId: p.id,
        sourceId: p.source_product_id || null,
        name: p.name,
        reup: Number(p.current_stock) || 0,
        counted: String(Number(p.current_stock) || 0),
      }))
  );
  const [masters, setMasters] = useState([]);
  const [addId, setAddId] = useState("");
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  useEffect(() => {
    api.get("/products")
      .then(({ data }) => setMasters((data || []).filter((m) => !m.archived)))
      .catch(() => {});
  }, []);

  const linkedSourceIds = useMemo(() => new Set(rows.map((r) => r.sourceId).filter(Boolean)), [rows]);
  const addable = useMemo(
    () => masters
      .filter((m) => !linkedSourceIds.has(m.id))
      .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [masters, linkedSourceIds]
  );

  const deltas = rows.map((r) => {
    const counted = Math.max(0, Math.floor(Number(r.counted)));
    return Number.isFinite(counted) ? counted - r.reup : 0;
  });
  const changeCount = deltas.filter((d) => d !== 0).length;

  const addMasterRow = () => {
    const m = masters.find((x) => x.id === addId);
    if (!m) return;
    setRows((prev) => [...prev, { childId: null, sourceId: m.id, name: m.name, reup: 0, counted: "0" }]);
    setAddId("");
  };

  const apply = async () => {
    setBusy(true);
    const out = [];
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      const delta = deltas[i];
      if (!delta) continue;
      try {
        if (delta < 0) {
          await api.post("/products/return", {
            distributor_id: distributorId,
            product_id: r.childId,
            quantity: -delta,
          });
          out.push({ name: r.name, delta, ok: true });
        } else {
          if (!r.sourceId) throw new Error(t("distributors.reconcile_no_source"));
          await api.post("/products/assign", {
            distributor_id: distributorId,
            product_id: r.sourceId,
            quantity: delta,
            transfer_type: "consignment",
            force,
            note: "Inventory reconcile — physical count",
          });
          out.push({ name: r.name, delta, ok: true });
        }
      } catch (e) {
        out.push({ name: r.name, delta, ok: false, error: e?.response ? formatApiError(e) : e.message });
      }
    }
    setBusy(false);
    setResults(out);
    const failed = out.filter((x) => !x.ok).length;
    if (failed === 0) toast.success(t("distributors.reconcile_done_toast", { n: out.length }));
    else toast.error(t("distributors.reconcile_failed_toast", { n: failed }));
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="reconcile-dialog"
      >
        <div className="p-5 pb-3 border-b border-zinc-100">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Scales size={20} weight="bold" className="text-[#c9a24a]" />
            {t("distributors.reconcile_title", { name: distributorName })}
          </h2>
          <p className="text-sm text-zinc-500 mt-1">{t("distributors.reconcile_sub")}</p>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-1">
          {results ? (
            <div className="space-y-2" data-testid="reconcile-results">
              {results.length === 0 && <p className="text-sm font-medium">{t("distributors.reconcile_none")}</p>}
              {results.map((r, i) => (
                <div key={i} className="flex items-center gap-2 text-sm border border-zinc-100 rounded-lg px-3 py-2">
                  {r.ok
                    ? <CheckCircle size={18} weight="fill" className="text-emerald-600 shrink-0" />
                    : <XCircle size={18} weight="fill" className="text-red-600 shrink-0" />}
                  <span className="font-medium flex-1 min-w-0 truncate">{r.name}</span>
                  <span className={`font-semibold tabular-nums ${r.delta < 0 ? "text-amber-700" : "text-emerald-700"}`}>
                    {r.delta < 0
                      ? t("distributors.reconcile_returned", { n: -r.delta })
                      : t("distributors.reconcile_pulled", { n: r.delta })}
                  </span>
                  {!r.ok && <span className="text-xs text-red-600 max-w-[40%] truncate" title={r.error}>{r.error}</span>}
                </div>
              ))}
            </div>
          ) : (
            <>
              {rows.map((r, i) => {
                const delta = deltas[i];
                return (
                  <div key={r.childId || r.sourceId} className="border border-zinc-100 rounded-lg px-2.5 py-2 space-y-1.5">
                    <p className="text-sm font-medium leading-tight" title={r.name}>{r.name}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[11px] text-zinc-400 whitespace-nowrap">
                        {t("distributors.reconcile_reup")}: <b className="text-zinc-700 tabular-nums">{r.reup}</b>
                      </span>
                      <BrutalInput
                        type="number"
                        min="0"
                        value={r.counted}
                        onChange={(e) => setRows((prev) => prev.map((x, j) => (j === i ? { ...x, counted: e.target.value } : x)))}
                        className="!py-1 !w-20 text-center ml-auto"
                        data-testid={`reconcile-counted-${i}`}
                      />
                      <span className={`text-[12px] font-semibold text-right min-w-[104px] ${delta === 0 ? "text-zinc-300" : delta < 0 ? "text-amber-700" : "text-emerald-700"}`}
                        data-testid={`reconcile-delta-${i}`}>
                        {delta === 0
                          ? t("distributors.reconcile_match")
                          : delta < 0
                            ? t("distributors.reconcile_return", { n: -delta })
                            : t("distributors.reconcile_pull", { n: delta })}
                      </span>
                    </div>
                  </div>
                );
              })}
              {addable.length > 0 && (
                <div className="flex gap-2 items-center pt-3">
                  <select
                    value={addId}
                    onChange={(e) => setAddId(e.target.value)}
                    className="flex-1 border border-zinc-200 rounded-lg px-2 py-2 text-sm bg-white"
                    data-testid="reconcile-add-select"
                  >
                    <option value="">{t("distributors.reconcile_add")}</option>
                    {addable.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                  <BrutalButton type="button" size="sm" variant="outline" onClick={addMasterRow} disabled={!addId} data-testid="reconcile-add-btn">
                    +
                  </BrutalButton>
                </div>
              )}
            </>
          )}
        </div>

        <div className="p-5 pt-3 border-t border-zinc-100 space-y-3">
          {!results && (
            <label className="flex items-center gap-2 text-xs text-zinc-600 cursor-pointer">
              <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)}
                className="w-4 h-4 accent-[#c9a24a]" data-testid="reconcile-force-checkbox" />
              {t("distributors.reconcile_force")}
            </label>
          )}
          <div className="flex gap-3">
            <BrutalButton type="button" variant="secondary" className="flex-1" onClick={results ? () => { onDone(); } : onClose}>
              {results ? t("common.close") : t("common.cancel")}
            </BrutalButton>
            {!results && (
              <BrutalButton
                type="button"
                className="flex-1"
                disabled={busy || changeCount === 0}
                onClick={apply}
                data-testid="reconcile-apply-btn"
              >
                {busy ? t("distributors.reconcile_processing") : t("distributors.reconcile_apply", { n: changeCount })}
              </BrutalButton>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
